import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import {
  getDocumentDisplayFields,
  guessViewerKind,
} from "utils/missionDocuments";

function documentIconName(documentName) {
  const kind = guessViewerKind(documentName);
  if (kind === "pdf") return "picture_as_pdf";
  if (kind === "image") return "image";
  if (kind === "video") return "movie";
  const lower = String(documentName || "").toLowerCase();
  if (/\.(doc|docx)$/.test(lower)) return "description";
  if (/\.(xls|xlsx|csv)$/.test(lower)) return "table_chart";
  return "insert_drive_file";
}

function documentTypeLabel(documentName) {
  const match = String(documentName || "").match(/\.([a-z0-9]{1,8})$/i);
  return match ? match[1].toUpperCase() : "FILE";
}

function formatDocumentDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function documentDetailRows(doc) {
  return (doc.displayFields ?? getDocumentDisplayFields(doc)).filter((row) => {
    const label = String(row.label ?? "").toLowerCase();
    if (["file", "key"].includes(label)) return false;
    const path = (doc.documentPath || doc.key || "").trim();
    const value = String(row.value ?? "").trim();
    if (!value || value === "—") return false;
    if (path && value === path) return false;
    return true;
  });
}

function DocumentCard({ doc, canAccess, isBusy, onView, onDownload }) {
  const documentName = doc.documentName || "Document";
  const fileIcon = documentIconName(documentName);
  const fileType = documentTypeLabel(documentName);
  const uploadedDate = formatDocumentDate(doc.createdAt);
  const detailRows = documentDetailRows(doc);
  const metaLine = [fileType, uploadedDate].filter(Boolean).join(" · ");

  return (
    <SoftBox
      display="flex"
      flexDirection="column"
      height="100%"
      p={2}
      sx={(theme) => ({
        height: "100%",
        borderRadius: "0.875rem",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0 2px 10px rgba(15, 34, 58, 0.05)",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          boxShadow: "0 6px 18px rgba(15, 34, 58, 0.08)",
          borderColor: theme.palette.info.main,
        },
      })}
    >
      <SoftBox display="flex" alignItems="flex-start" gap={1.5}>
        <SoftBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={48}
          height={48}
          borderRadius="lg"
          flexShrink={0}
          sx={({ palette, functions: { linearGradient } }) => ({
            backgroundImage: linearGradient(
              palette.info.main,
              palette.info.focus,
            ),
            color: palette.white.main,
            boxShadow: "0 4px 12px rgba(0, 184, 217, 0.25)",
          })}
        >
          <Icon fontSize="medium">{fileIcon}</Icon>
        </SoftBox>

        <SoftBox flex={1} minWidth={0} pt={0.25}>
          <SoftTypography
            variant="button"
            fontWeight="bold"
            color="dark"
            display="block"
            title={documentName}
            sx={{
              lineHeight: 1.35,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word",
            }}
          >
            {documentName}
          </SoftTypography>
          {metaLine ? (
            <SoftTypography
              variant="caption"
              color="secondary"
              display="block"
              mt={0.5}
            >
              {metaLine}
            </SoftTypography>
          ) : null}
        </SoftBox>
      </SoftBox>

      {detailRows.length > 0 ? (
        <SoftBox
          mt={1.5}
          pt={1.5}
          sx={(theme) => ({
            borderTop: `1px solid ${theme.palette.divider}`,
          })}
        >
          {detailRows.slice(0, 2).map((row) => (
            <SoftBox key={`${row.label}-${row.value}`} mb={0.75}>
              <SoftTypography
                variant="caption"
                color="secondary"
                fontWeight="medium"
                display="block"
              >
                {row.label}
              </SoftTypography>
              <SoftTypography
                variant="caption"
                color="text"
                display="block"
                sx={{ wordBreak: "break-word" }}
              >
                {row.value}
              </SoftTypography>
            </SoftBox>
          ))}
        </SoftBox>
      ) : null}

      {canAccess ? (
        <SoftBox
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={0.75}
          mt="auto"
          pt={detailRows.length ? 1.25 : 1.5}
        >
          <SoftButton
            variant="outlined"
            color="info"
            size="small"
            disabled={isBusy}
            onClick={() => onView(doc)}
            startIcon={<Icon fontSize="small">visibility</Icon>}
            sx={{ minWidth: 0, px: 1.5 }}
          >
            View
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="info"
            size="small"
            disabled={isBusy}
            onClick={() => onDownload(doc)}
            startIcon={
              isBusy ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <Icon fontSize="small">download</Icon>
              )
            }
            sx={{ minWidth: 0, px: 1.5 }}
          >
            Download
          </SoftButton>
        </SoftBox>
      ) : null}
    </SoftBox>
  );
}

DocumentCard.propTypes = {
  doc: PropTypes.object.isRequired,
  canAccess: PropTypes.bool,
  isBusy: PropTypes.bool,
  onView: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
};

export default DocumentCard;
